import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOffline } from '../contexts/OfflineContext';
import stocktakeOfflineService, { SYNC_STATES } from '../services/stocktakeOfflineService';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Download,
  RefreshCw,
  Trash2,
  WifiOff,
  Wifi,
  CheckCircle,
  AlertTriangle,
  Clock,
  CloudUpload,
  HardDrive,
  ArrowRight,
  ArrowLeft,
  Shield,
  Smartphone,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import OfflinePackageDiagnostics from '../components/OfflinePackageDiagnostics';

export default function OfflineSessionsPage() {
  const navigate = useNavigate();
  const {
    isOnline,
    isSyncing,
    offlineSessions,
    refreshOfflineSessions,
    syncStocktakeSession,
    syncAll,
    swUpdateAvailable,
    applySwUpdate,
  } = useOffline();

  const [removing, setRemoving] = useState(null);
  const [expandedSession, setExpandedSession] = useState(null);

  useEffect(() => {
    refreshOfflineSessions();
  }, [refreshOfflineSessions]);

  const handleSync = useCallback(async (sessionId) => {
    const result = await syncStocktakeSession(sessionId);
    if (result.sync_status === 'synced') {
      toast.success('Session synced successfully');
    } else if (result.sync_status === 'conflict') {
      toast.error('Conflict detected — session was modified on the server');
    } else {
      toast.error(result.error || 'Sync failed');
    }
  }, [syncStocktakeSession]);

  const handleRemove = useCallback(async (sessionId) => {
    setRemoving(sessionId);
    await stocktakeOfflineService.removeOfflinePackage(sessionId);
    await refreshOfflineSessions();
    setRemoving(null);
    toast.success('Offline session removed');
  }, [refreshOfflineSessions]);

  const handleOpenOffline = useCallback((sessionId) => {
    navigate(`/stocktaking/sessions/${sessionId}?offline=true`);
  }, [navigate]);

  const getSyncBadge = (state) => {
    switch (state) {
      case SYNC_STATES.SYNCED:
        return <Badge data-testid="sync-badge-synced" className="bg-emerald-100 text-emerald-700 border-emerald-200"><CheckCircle className="h-3 w-3 mr-1" />Synced</Badge>;
      case SYNC_STATES.PENDING:
        return <Badge data-testid="sync-badge-pending" className="bg-amber-100 text-amber-700 border-amber-200"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case SYNC_STATES.CONFLICT:
        return <Badge data-testid="sync-badge-conflict" variant="destructive"><AlertTriangle className="h-3 w-3 mr-1" />Conflict</Badge>;
      case SYNC_STATES.ERROR:
        return <Badge data-testid="sync-badge-error" variant="destructive"><AlertTriangle className="h-3 w-3 mr-1" />Error</Badge>;
      default:
        return <Badge variant="secondary">{state}</Badge>;
    }
  };

  const pendingSessions = offlineSessions.filter(s => s.sync_state === SYNC_STATES.PENDING);
  const conflictSessions = offlineSessions.filter(s => s.sync_state === SYNC_STATES.CONFLICT);

  return (
    <div className="space-y-6" data-testid="offline-sessions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/stocktaking/sessions')} data-testid="back-to-sessions-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Offline Sessions</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage stocktake sessions available for offline work
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm">
            {isOnline ? (
              <><Wifi className="h-4 w-4 text-emerald-500" /><span className="text-emerald-600 font-medium">Online</span></>
            ) : (
              <><WifiOff className="h-4 w-4 text-amber-500" /><span className="text-amber-600 font-medium">Offline</span></>
            )}
          </div>
          {pendingSessions.length > 0 && isOnline && (
            <Button
              data-testid="sync-all-btn"
              onClick={syncAll}
              disabled={isSyncing}
              size="sm"
            >
              <CloudUpload className={`h-4 w-4 mr-1 ${isSyncing ? 'animate-spin' : ''}`} />
              {isSyncing ? 'Syncing...' : `Sync All (${pendingSessions.length})`}
            </Button>
          )}
        </div>
      </div>

      {/* SW Update Banner */}
      {swUpdateAvailable && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">A new version of AbbeyZone is available</span>
            </div>
            <Button size="sm" variant="outline" onClick={applySwUpdate} data-testid="apply-update-btn">
              <RefreshCw className="h-3 w-3 mr-1" />Update Now
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Conflict Warning */}
      {conflictSessions.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <span className="text-sm font-medium text-red-800">
                {conflictSessions.length} session{conflictSessions.length > 1 ? 's have' : ' has'} sync conflicts.
                The server version was modified after your download. Re-download the session to resolve.
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sessions List */}
      {offlineSessions.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <HardDrive className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-40" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">No offline sessions</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
              Download a stocktake session from the Sessions page to work offline.
              Open any session and click "Download for Offline".
            </p>
            <Button variant="outline" onClick={() => navigate('/stocktaking/sessions')} data-testid="go-to-sessions-btn">
              Go to Sessions
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {offlineSessions.map((session) => (
            <Card key={session.id} className="hover:shadow-md transition-shadow" data-testid={`offline-session-${session.id}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-semibold text-slate-900 truncate">
                        {session.session_reference}
                      </h3>
                      {getSyncBadge(session.sync_state)}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span>{session.site_name}</span>
                      {session.session_date && (
                        <span>{format(new Date(session.session_date), 'd MMM yyyy')}</span>
                      )}
                      <span className="capitalize">{session.session_type?.replace('_', ' ')}</span>
                    </div>
                    {session.downloaded_at && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Downloaded: {format(new Date(session.downloaded_at), 'd MMM yyyy HH:mm')}
                        {session.last_synced_at && ` · Last synced: ${format(new Date(session.last_synced_at), 'd MMM HH:mm')}`}
                      </p>
                    )}
                    {session.conflict_details && (
                      <p className="text-xs text-red-600 mt-1">
                        {session.conflict_details.reason || 'Conflict detected'}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      data-testid={`inspect-offline-session-${session.id}`}
                      size="sm"
                      variant="ghost"
                      onClick={() => setExpandedSession(expandedSession === session.id ? null : session.id)}
                    >
                      {expandedSession === session.id ? 'Hide' : 'Inspect'}
                    </Button>
                    {session.sync_state === SYNC_STATES.PENDING && isOnline && (
                      <Button
                        data-testid={`sync-session-${session.id}`}
                        size="sm"
                        variant="outline"
                        onClick={() => handleSync(session.id)}
                        disabled={isSyncing}
                      >
                        <CloudUpload className={`h-4 w-4 mr-1 ${isSyncing ? 'animate-spin' : ''}`} />
                        Sync
                      </Button>
                    )}
                    <Button
                      data-testid={`open-offline-session-${session.id}`}
                      size="sm"
                      onClick={() => handleOpenOffline(session.id)}
                    >
                      Open
                      <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                    <Button
                      data-testid={`remove-offline-session-${session.id}`}
                      size="sm"
                      variant="ghost"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleRemove(session.id)}
                      disabled={removing === session.id}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {/* Expandable diagnostics panel */}
                {expandedSession === session.id && (
                  <div className="mt-4 border-t pt-4">
                    <OfflinePackageDiagnostics sessionId={session.id} />
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

    </div>
  );
}
